import {
  SOURCE_CONFIG_FIELD,
  type AggregatedAnalyticsData,
  type AnalyticsSourceKey,
  type IntelligenceReport,
  type IntelligenceReportVersion,
  type MerchantAnalyticsConfig,
} from './types';

/**
 * Why this file exists.
 *
 * The report is aggregated from four independent analytics sources, and the
 * backend's `aggregate()` collapses two very different failures into the same
 * `null`:
 *
 *   1. no property id configured for this merchant  -> source never attempted
 *   2. id configured, but the upstream call threw   -> attempted, returned nothing
 *
 * and a THIRD case looks similar but means the opposite:
 *
 *   3. source returned a payload whose numbers are all zero -> the source works,
 *      the merchant genuinely had no activity that month.
 *
 * Telling a merchant "you had no sessions" when the truth is "we never asked
 * Google" is the specific dishonesty this feature has to avoid, so the UI
 * derives an explicit verdict per source instead of rendering a bare dash.
 *
 * As of the live dev probe, NO merchant has an analytics config row at all
 * (every id null), so `NOT_CONNECTED` is the expected verdict across the estate
 * today — the empty states are the common path here, not an edge case.
 */
export type SourceStatus =
  /** No id configured — the source was never queried. */
  | 'NOT_CONNECTED'
  /** Configured, but the aggregate holds no payload — the upstream call failed. */
  | 'NO_RESPONSE'
  /** Data returned, and at least one headline figure is non-zero. */
  | 'OK'
  /** Data returned, but every headline figure is zero — genuinely no activity. */
  | 'NO_ACTIVITY';

export interface SourceProvenance {
  source: AnalyticsSourceKey;
  status: SourceStatus;
  /** Headline figures for the source, already formatted. Empty unless OK/NO_ACTIVITY. */
  metrics: Array<{ label: string; value: string }>;
}

const SOURCES: AnalyticsSourceKey[] = ['ga4', 'sentry', 'clarity', 'meta'];

/** 0–1 fraction -> "54.0%". Matches the backend writer's own `percent()`. */
export const formatRate = (value: number): string => `${(value * 100).toFixed(1)}%`;

const counts = new Intl.NumberFormat('en-NG');
export const formatCount = (value: number): string => counts.format(value);

/** Seconds -> "1m 04s" / "48s", for GA4's average session duration. */
export const formatDuration = (seconds: number): string => {
  const whole = Math.max(0, Math.round(seconds));
  const mins = Math.floor(whole / 60);
  const secs = whole % 60;
  return mins > 0 ? `${mins}m ${String(secs).padStart(2, '0')}s` : `${secs}s`;
};

type Ga4Like = NonNullable<AggregatedAnalyticsData['ga4']>;
type SentryLike = NonNullable<AggregatedAnalyticsData['sentry']>;
type ClarityLike = NonNullable<AggregatedAnalyticsData['clarity']>;
type MetaLike = NonNullable<AggregatedAnalyticsData['meta']>;

type SourcePayload = Ga4Like | SentryLike | ClarityLike | MetaLike;

/**
 * The figures that decide OK vs NO_ACTIVITY, per source. Deliberately the
 * *volume* metrics only: a bounce rate of 0 alongside 0 sessions is not evidence
 * of anything, whereas 0 sessions is. Meta's `spend` is excluded — a campaign can
 * legitimately run at zero spend, and it is the one money field here (see the
 * warning on `MetaData.spend`).
 */
const activitySignals = (source: AnalyticsSourceKey, payload: SourcePayload): number[] => {
  switch (source) {
    case 'ga4': {
      const d = payload as Ga4Like;
      return [d.sessions, d.users];
    }
    case 'sentry': {
      const d = payload as SentryLike;
      return [d.totalErrors, d.uniqueErrors];
    }
    case 'clarity': {
      const d = payload as ClarityLike;
      return [d.totalSessions, d.engagedSessions];
    }
    case 'meta': {
      const d = payload as MetaLike;
      return [d.impressions, d.reach, d.clicks];
    }
  }
};

const ga4Metrics = (d: Ga4Like) => [
  { label: 'Sessions', value: formatCount(d.sessions) },
  { label: 'Users', value: formatCount(d.users) },
  { label: 'New users', value: formatCount(d.newUsers) },
  { label: 'Bounce rate', value: formatRate(d.bounceRate) },
  { label: 'Avg. session', value: formatDuration(d.avgSessionDuration) },
  {
    label: 'Conversion rate',
    // `null` here is its own fact: GA4 answered, but no conversion goal is set
    // up on the property. "Not configured" is the honest label, not "0%".
    value: d.conversionRate === null ? 'Not configured' : formatRate(d.conversionRate),
  },
];

const sentryMetrics = (d: SentryLike) => [
  { label: 'Total errors', value: formatCount(d.totalErrors) },
  { label: 'Unique issues', value: formatCount(d.uniqueErrors) },
  { label: 'Performance issues', value: formatCount(d.performanceIssues) },
  {
    label: 'Crash-free sessions',
    // Already a percentage server-side (`toFixed(2)` on the raw value), unlike
    // every other rate in this payload which is a 0–1 fraction. Do not run it
    // through formatRate or it becomes 100x too large.
    value: d.crashFreeSessionRate === null ? 'Not available' : `${d.crashFreeSessionRate.toFixed(2)}%`,
  },
];

const clarityMetrics = (d: ClarityLike) => [
  { label: 'Sessions', value: formatCount(d.totalSessions) },
  { label: 'Engaged sessions', value: formatCount(d.engagedSessions) },
  { label: 'Rage clicks', value: formatCount(d.rageclicks) },
  { label: 'Dead clicks', value: formatCount(d.deadClicks) },
  { label: 'Excessive scrolling', value: formatCount(d.excessiveScrolling) },
];

const metaMetrics = (d: MetaLike) => [
  { label: 'Impressions', value: formatCount(d.impressions) },
  { label: 'Reach', value: formatCount(d.reach) },
  { label: 'Clicks', value: formatCount(d.clicks) },
  { label: 'CTR', value: d.ctr === null ? 'Not available' : formatRate(d.ctr) },
  {
    label: 'Conversions',
    value: d.conversions === null ? 'Not available' : formatCount(d.conversions),
  },
  // `spend` intentionally omitted — no currency and no unit marker travels with
  // it, so there is no honest way to render it as money. See types.ts.
];

const METRIC_BUILDERS = {
  ga4: ga4Metrics,
  sentry: sentryMetrics,
  clarity: clarityMetrics,
  meta: metaMetrics,
} as const;

/**
 * The AI_DRAFT version is the only one that carries `rawData` (the released copy
 * is created from content alone), so provenance always reads the draft.
 */
export const draftVersion = (
  report: Pick<IntelligenceReport, 'versions'> | undefined | null,
): IntelligenceReportVersion | undefined =>
  report?.versions?.find((v) => v.versionType === 'AI_DRAFT');

/**
 * The version whose content is authoritative for display: the RELEASED copy when
 * one exists (it reflects any TCS edits), otherwise the untouched AI draft.
 * Mirrors the backend's own precedence in `exportPdf`.
 */
export const displayVersion = (
  report: Pick<IntelligenceReport, 'versions'> | undefined | null,
): IntelligenceReportVersion | undefined =>
  report?.versions?.find((v) => v.versionType === 'RELEASED') ?? draftVersion(report);

/** True once a TCS admin's edited copy has been stored separately from the draft. */
export const hasEditedVersion = (
  report: Pick<IntelligenceReport, 'versions'> | undefined | null,
): boolean => {
  const draft = draftVersion(report);
  const released = report?.versions?.find((v) => v.versionType === 'RELEASED');
  return !!draft && !!released && draft.content !== released.content;
};

/**
 * Per-source verdict for one report.
 *
 * `config` may be undefined (not loaded, or the merchant has no config row). In
 * that case a missing payload cannot be pinned on a missing id, so it degrades
 * to NO_RESPONSE rather than asserting NOT_CONNECTED — the UI must not claim a
 * source is disconnected on evidence it does not have.
 */
export const sourceProvenance = (
  rawData: AggregatedAnalyticsData | null | undefined,
  config: MerchantAnalyticsConfig | undefined | null,
): SourceProvenance[] =>
  SOURCES.map((source) => {
    const payload = rawData?.[source] ?? null;

    if (!payload) {
      const configuredId = config ? config[SOURCE_CONFIG_FIELD[source]] : undefined;
      const knownUnconfigured = !!config && !configuredId;
      return {
        source,
        status: knownUnconfigured ? 'NOT_CONNECTED' : 'NO_RESPONSE',
        metrics: [],
      };
    }

    const anyActivity = activitySignals(source, payload).some((n) => Number(n) > 0);
    const build = METRIC_BUILDERS[source] as (d: SourcePayload) => Array<{ label: string; value: string }>;

    return {
      source,
      status: anyActivity ? 'OK' : 'NO_ACTIVITY',
      metrics: build(payload),
    };
  });

/** Copy for each verdict. Kept beside the logic so the two can't drift apart. */
export const STATUS_COPY: Record<SourceStatus, { label: string; tone: string; detail: string }> = {
  NOT_CONNECTED: {
    label: 'Not connected',
    tone: 'bg-muted text-muted-foreground',
    detail: 'No property ID is configured, so this source was never queried for this period.',
  },
  NO_RESPONSE: {
    label: 'No data returned',
    tone: 'bg-amber-100 text-amber-800',
    detail: 'This source was queried but returned nothing — the upstream call failed or was unavailable.',
  },
  NO_ACTIVITY: {
    label: 'No activity',
    tone: 'bg-blue-100 text-blue-700',
    detail: 'This source responded successfully and recorded no activity for this merchant this period.',
  },
  OK: {
    label: 'Data collected',
    tone: 'bg-emerald-100 text-emerald-700',
    detail: 'This source returned data for the period.',
  },
};

/** True when nothing at all reached the report — drives the top-level warning. */
export const hasNoSourceData = (provenance: SourceProvenance[]): boolean =>
  provenance.every((p) => p.status === 'NOT_CONNECTED' || p.status === 'NO_RESPONSE');
